import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import DirectionDial from '../../utils/DirectionDial';

interface RotateCoreLineDialogProps {
  open: boolean;
  /** The sample whose core line is being rotated (shown in the title). */
  sampleId: string;
  /** Direction the sample's core line is currently walked in (0–360). */
  initialDirectionDeg: number;
  onClose: () => void;
  onApply: (directionDeg: number) => void | Promise<void>;
}

/** Fold a typed angle into the dial's 0–360 range. */
const normalizeDialAngle = (angleDeg: number): number => {
  if (!Number.isFinite(angleDeg)) {
    return 0;
  }

  return ((Math.round(angleDeg) % 360) + 360) % 360;
};

/** Draft-direction nudges offered next to the dial. */
const NUDGE_DEGREES = 45;

/**
 * Rotates one sample's core line. Uses the Smart Path Settings direction dial,
 * because the meaningful value is the direction the path walks the line in:
 * setting the opposite direction (200° for a line walked at 20°) is a half
 * turn — the line stays exactly where it is and its first and last cores swap.
 */
const RotateCoreLineDialog = ({ open, sampleId, initialDirectionDeg, onClose, onApply }: RotateCoreLineDialogProps) => {
  const [directionDeg, setDirectionDeg] = useState(() => normalizeDialAngle(initialDirectionDeg));

  // Seed the dial from the selected sample: opening the dialog for another
  // sample must not carry the previous sample's draft direction over.
  useEffect(() => {
    if (open) {
      setDirectionDeg(normalizeDialAngle(initialDirectionDeg));
    }
  }, [open, sampleId, initialDirectionDeg]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Rotate core line</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {`Sample ${sampleId}`}
        </Typography>

        <Box display="flex" justifyContent="center" mt={1}>
          <DirectionDial angleDegrees={directionDeg} onChange={(value) => setDirectionDeg(normalizeDialAngle(value))} />
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={1}>
          Arrow shows the direction the path walks the line in — first core visited to last.
        </Typography>

        <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
          <TextField
            label="direction"
            type="number"
            size="small"
            value={directionDeg}
            onChange={(event) => setDirectionDeg(normalizeDialAngle(parseInt(event.target.value || '0')))}
            inputProps={{ min: 0, max: 359, step: 1, 'aria-label': 'direction' }}
            sx={{ maxWidth: 140 }}
          />
        </Box>

        <Box display="flex" justifyContent="center" alignItems="center" gap={1} mt={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setDirectionDeg((current) => normalizeDialAngle(current - NUDGE_DEGREES))}
            title={`Rotate the line ${NUDGE_DEGREES}° clockwise`}
          >
            -45°
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setDirectionDeg((current) => normalizeDialAngle(current + 180))}
            title="Keep the line where it is and swap its first and last core"
          >
            Flip 180°
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setDirectionDeg((current) => normalizeDialAngle(current + NUDGE_DEGREES))}
            title={`Rotate the line ${NUDGE_DEGREES}° counter-clockwise`}
          >
            +45°
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={2}>
          Recalculating the path rebuilds this sample&apos;s line from the mission angle.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="secondary" onClick={() => onApply(normalizeDialAngle(directionDeg))}>
          Rotate
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RotateCoreLineDialog;
